import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import {
	Box,
	Button,
	CircularProgress,
	Grid2,
	Skeleton,
	TextField,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Group, Panel, Separator } from "react-resizable-panels";

import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import {
	DrawingViewer,
	DrawingViewerHandle,
} from "@/pages/products/components/DrawingViewer";
import { CLIP_INITIAL_VALUES, Clip } from "@/shared/models/Clip";
import { deleteClip, getClip } from "@/shared/repositories/ClipRepository";
import { ClipDetailFields } from "./ClipDetailFields";
import { useClipMutations } from "../hooks/useClipMutations";

export type ClipModalMode = "create" | "update" | "delete" | null;

type Props = {
	mode: ClipModalMode;
	clipId: number | null;
	onClose: (shouldRefresh: boolean, newClipId?: number) => void;
};

export const UpsertDeleteModal = ({ mode, clipId, onClose }: Props) => {
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");
	const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
	const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
	// True when UpsertClip succeeded but SetClipPunchSlots failed.
	const [slotsRetryPending, setSlotsRetryPending] = useState(false);
	// In create mode, after a successful create the modal flips internally to
	// update mode for subsequent retries. This holds the new id.
	const [internalClipId, setInternalClipId] = useState<number | null>(null);
	// Mirrors the viewer's own dirty state (zoom/pan vs saved view).
	const [viewDirty, setViewDirty] = useState(false);

	const closeAfterSaveRef = useRef(false);
	const dirtyRef = useRef(false);
	const viewerRef = useRef<DrawingViewerHandle>(null);

	const isOpen = mode !== null;
	const effectiveMode: "create" | "update" | "delete" =
		mode === "create" && internalClipId != null ? "update" : (mode ?? "update");
	const effectiveClipId = internalClipId ?? clipId;

	const {
		control,
		reset,
		handleSubmit,
		setValue,
		watch,
		getValues,
		formState: { isDirty, dirtyFields },
	} = useForm<Clip>({ defaultValues: CLIP_INITIAL_VALUES });

	const currentDrawingPath = watch("DrawingPath");

	const { data: clipDetail, isLoading } = useQuery<Clip | null, Error>({
		queryKey: ["clip", effectiveClipId],
		queryFn: () =>
			effectiveClipId ? getClip(effectiveClipId) : Promise.resolve(null),
		enabled: isOpen && effectiveClipId != null,
		onError: (e) => setError(e.message),
	});

	// Reset form whenever the modal opens or its target changes.
	useEffect(() => {
		if (!isOpen) return;
		if (effectiveMode === "create" && internalClipId == null) {
			reset(CLIP_INITIAL_VALUES);
			setSlotsRetryPending(false);
		} else if (clipDetail) {
			const initialSlots =
				clipDetail.Slots && clipDetail.Slots.length > 0
					? clipDetail.Slots
					: CLIP_INITIAL_VALUES.Slots;
			reset({ ...clipDetail, Slots: initialSlots });
		}
	}, [isOpen, effectiveMode, internalClipId, clipDetail, reset]);

	// Reset transient state when the modal closes / target changes from outside.
	useEffect(() => {
		if (!isOpen) {
			setInternalClipId(null);
			setSlotsRetryPending(false);
			setViewDirty(false);
			dirtyRef.current = false;
			closeAfterSaveRef.current = false;
		}
	}, [isOpen]);

	const getDirtyValues = useCallback((): Partial<Clip> => {
		const values = getValues();
		const dirty: Partial<Clip> = {};
		const assign = <K extends keyof Clip>(k: K) => {
			dirty[k] = values[k];
		};
		for (const key of Object.keys(dirtyFields) as (keyof Clip)[]) {
			if (dirtyFields[key]) {
				assign(key);
			}
		}
		return dirty;
	}, [dirtyFields, getValues]);

	const isSlotsDirty = useCallback((): boolean => {
		const slots = dirtyFields.Slots;
		if (!slots) return false;
		if (Array.isArray(slots)) return slots.length > 0;
		return true;
	}, [dirtyFields]);

	const isSlotsRetryPending = useCallback(
		() => slotsRetryPending,
		[slotsRetryPending],
	);

	const closeWithRefresh = useCallback(
		(saved: boolean, newClipId?: number) => {
			const shouldRefresh = saved || dirtyRef.current;
			onClose(shouldRefresh, newClipId);
		},
		[onClose],
	);

	const { upsertMutation, isPending } = useClipMutations({
		mode: effectiveMode === "delete" ? "update" : effectiveMode,
		getDirtyValues,
		isSlotsDirty,
		isSlotsRetryPending,
		onSuccess: (message, savedClipId) => {
			setNotificationMessage(message);
			setSlotsRetryPending(false);
			dirtyRef.current = true;
			reset(getValues());
			if (closeAfterSaveRef.current) {
				closeAfterSaveRef.current = false;
				const wasCreate = mode === "create" && internalClipId == null;
				closeWithRefresh(true, wasCreate ? savedClipId : undefined);
			}
		},
		onError: (err) => setError(err),
		onSlotsError: (msg, savedClipId) => {
			// Parent persisted; align form values to clear parent dirty state, then mark
			// slots retry. In create mode this also flips the modal to update mode for
			// the new clipId.
			reset(getValues());
			dirtyRef.current = true;
			setSlotsRetryPending(true);
			if (mode === "create" && internalClipId == null) {
				setInternalClipId(savedClipId);
			}
			closeAfterSaveRef.current = false;
			setError(msg);
		},
	});

	const deleteMutation = useMutation({
		mutationFn: (id: number) => deleteClip(id),
		onSuccess: (resp) => {
			if (resp.ok) {
				dirtyRef.current = true;
				closeWithRefresh(true);
			} else {
				setError(resp.message || "Failed to delete clip");
			}
		},
		onError: (e: Error) => setError(e.message),
	});

	const onSubmit = async (data: Clip) => {
		if (viewDirty && (await viewerRef.current?.saveView()) === false) return;
		// A view-only change has nothing to upsert; don't touch the clip row.
		if (!isDirty && !slotsRetryPending) {
			setNotificationMessage("The drawing view was saved");
			if (closeAfterSaveRef.current) {
				closeAfterSaveRef.current = false;
				closeWithRefresh(false);
			}
			return;
		}
		upsertMutation.mutate(data);
	};

	const handleSaveClick = () => {
		closeAfterSaveRef.current = false;
		handleSubmit(onSubmit)();
	};

	const handleSaveAndCloseClick = () => {
		closeAfterSaveRef.current = true;
		handleSubmit(onSubmit)();
	};

	const handleCreateClick = () => {
		closeAfterSaveRef.current = true;
		handleSubmit(onSubmit)();
	};

	const handleDeleteClick = () => {
		setConfirmDeleteOpen(true);
	};

	const handleConfirmDelete = (value?: string) => {
		setConfirmDeleteOpen(false);
		if (value === "OK" && effectiveClipId != null) {
			deleteMutation.mutate(effectiveClipId);
		}
	};

	const requestClose = useCallback(() => {
		const dirty =
			effectiveMode !== "delete" && (isDirty || slotsRetryPending || viewDirty);
		if (dirty) {
			setConfirmDiscardOpen(true);
		} else {
			closeWithRefresh(false);
		}
	}, [effectiveMode, isDirty, slotsRetryPending, viewDirty, closeWithRefresh]);

	const handleConfirmDiscard = (value?: string) => {
		setConfirmDiscardOpen(false);
		if (value === "OK") {
			closeWithRefresh(false);
		}
	};

	const handleDrawingUpload = useCallback(
		(url: string) => {
			setValue("DrawingPath", url, { shouldDirty: true });
		},
		[setValue],
	);

	const handleDrawingClear = useCallback(() => {
		setValue("DrawingPath", null, { shouldDirty: true });
	}, [setValue]);

	const fieldsDisabled = effectiveMode === "delete" || isPending;
	const showSkeleton =
		isOpen &&
		effectiveMode !== "create" &&
		isLoading &&
		effectiveClipId != null;

	const title =
		mode === "create"
			? "NEW CLIP"
			: mode === "delete"
				? "DELETE CLIP"
				: "EDIT CLIP";

	const saveDisabled =
		isPending || (!isDirty && !slotsRetryPending && !viewDirty);

	return (
		<>
			<MesfModal
				id="modal-clip"
				title={title}
				open={isOpen}
				handleClose={requestClose}
				maxWidth={false}
			>
				<MesfModal.Content
					sx={{
						padding: "15px 24px",
						width: "min(calc(100vw - 64px), 1750px)",
						boxSizing: "border-box",
						height: "82vh",
						minWidth: 0,
						minHeight: 0,
						display: "flex",
						flexDirection: "column",
					}}
				>
					{showSkeleton ? (
						<Box sx={{ flex: 1, minHeight: 0, display: "flex", gap: 2 }}>
							<Box
								sx={{
									width: "60%",
									display: "flex",
									flexDirection: "column",
									gap: 2,
									pt: 1,
								}}
							>
								<Skeleton variant="text" width="40%" height={28} />
								<Skeleton variant="rectangular" height={40} />
								<Skeleton variant="rectangular" height={40} />
								<Skeleton variant="rectangular" height={40} />
								<Skeleton
									variant="text"
									width="50%"
									height={28}
									sx={{ mt: 1 }}
								/>
								<Skeleton variant="rectangular" height={40} />
								<Skeleton variant="rectangular" height={40} />
							</Box>
							<Skeleton
								variant="rectangular"
								sx={{ flex: 1, borderRadius: 1 }}
								height="100%"
							/>
						</Box>
					) : (
						<Box sx={{ flex: 1, minHeight: 0 }}>
							<Group orientation="horizontal" style={{ height: "100%" }}>
								<Panel id="clip-form" defaultSize={60} minSize={30}>
									<Box sx={{ overflow: "auto", height: "100%", pr: 1 }}>
										<Grid2 container spacing={1.5} columnSpacing={1.5}>
											<ClipDetailFields
												control={control}
												disabled={fieldsDisabled}
											/>
										</Grid2>
									</Box>
								</Panel>
								<Separator
									style={{
										width: 8,
										backgroundColor: "#e0e0e0",
										cursor: "col-resize",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
									}}
								>
									<Box
										sx={{
											width: 4,
											height: 40,
											backgroundColor: "#bdbdbd",
											borderRadius: 2,
										}}
									/>
								</Separator>
								<Panel id="clip-drawing" defaultSize={40} minSize={20}>
									<DrawingViewer
										ref={viewerRef}
										drawingPath={currentDrawingPath || null}
										onDrawingUpload={
											fieldsDisabled ? undefined : handleDrawingUpload
										}
										onDrawingClear={
											fieldsDisabled ? undefined : handleDrawingClear
										}
										onDirtyChange={setViewDirty}
										onError={setError}
										toolbarLeft={
											<Controller
												name="DrawingNumber"
												control={control}
												render={({ field }) => (
													<TextField
														{...field}
														value={field.value ?? ""}
														label="SWVA Drwg #"
														size="small"
														disabled={fieldsDisabled}
														InputProps={{ readOnly: fieldsDisabled }}
														sx={{ minWidth: 200 }}
													/>
												)}
											/>
										}
									/>
								</Panel>
							</Group>
						</Box>
					)}
				</MesfModal.Content>

				<MesfModal.Actions
					style={{
						padding: "16px 24px 24px",
						display: "flex",
						justifyContent: "flex-end",
						gap: 8,
					}}
				>
					<Button
						variant="contained"
						color="inherit"
						onClick={requestClose}
						disabled={isPending || deleteMutation.isPending}
					>
						Cancel
					</Button>

					{mode === "create" && (
						<Button
							variant="contained"
							color="primary"
							disabled={isPending}
							startIcon={
								isPending ? <CircularProgress size="1rem" /> : undefined
							}
							onClick={handleCreateClick}
						>
							Create
						</Button>
					)}

					{effectiveMode === "update" && mode !== "delete" && (
						<>
							<Button
								variant="contained"
								color="primary"
								disabled={saveDisabled}
								startIcon={
									isPending && !closeAfterSaveRef.current ? (
										<CircularProgress size="1rem" />
									) : undefined
								}
								onClick={handleSaveClick}
							>
								Save
							</Button>
							<Button
								variant="contained"
								color="primary"
								disabled={saveDisabled}
								startIcon={
									isPending && closeAfterSaveRef.current ? (
										<CircularProgress size="1rem" />
									) : undefined
								}
								onClick={handleSaveAndCloseClick}
							>
								Save &amp; Close
							</Button>
						</>
					)}

					{mode === "delete" && (
						<Button
							variant="contained"
							color="error"
							disabled={deleteMutation.isPending}
							startIcon={
								deleteMutation.isPending ? (
									<CircularProgress size="1rem" />
								) : undefined
							}
							onClick={handleDeleteClick}
						>
							Delete
						</Button>
					)}
				</MesfModal.Actions>
			</MesfModal>

			<ConfirmationDialogRaw
				title="Unsaved Changes"
				message="You have unsaved changes. If you leave now, your changes will be lost. Continue?"
				open={confirmDiscardOpen}
				onClose={handleConfirmDiscard}
			/>

			<ConfirmationDialogRaw
				title="Delete Clip"
				message="This will permanently delete the clip. Continue?"
				open={confirmDeleteOpen}
				onClose={handleConfirmDelete}
			/>

			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
